import {
  Children,
  cloneElement,
  isValidElement,
  useContext,
  type FC,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { CoreContext } from "../contexts/CoreContext";
import type { LastBotCore } from "../core/LastBotCore";
import {
  dispatchCustomTemplateEvent,
  runSlotAction,
} from "./dispatchSlotEvent";
import { Slot } from "./Slot";
import { SlotPlaceholder } from "./SlotPlaceholder";
import { getSlotDescriptor, type SlotId } from "./slotRegistry";
import { hasShellTemplate } from "./discoverTemplates";
import { TemplatedScopeProvider } from "./TemplatedScope";
import { useTemplate } from "./useTemplate";

/**
 * Derives the slot-registry id implied by a `<Templated id="lastbot-X">`.
 * Returns `null` for ids that don't map to a registered slot
 * (e.g. `lastbot-app`, which is a top-level shell with no slot equivalent).
 *
 * Used by `Templated` to attach a `data-slot-name="X"` marker on every
 * templated shell that corresponds to a slot in the registry — so the
 * outer bubble of `<Templated id="lastbot-message-bubble-user">` carries
 * `data-slot-name="message-bubble-user"`, the navbar shell carries
 * `data-slot-name="navbar"`, etc. Embedders can target shells the same
 * way they target slots inside them.
 */
const templatedSlotId = (id: string): string | null => {
  if (!id.startsWith("lastbot-")) return null;
  const slotId = id.slice("lastbot-".length);
  return getSlotDescriptor(slotId) !== undefined ? slotId : null;
};

interface TemplatedProps {
  id: string;
  children: ReactNode;
  /**
   * Props to apply to the *root element* of the parsed template tree when a host-page
   * template overrides this component. Use this for interactive components whose template
   * is the click target (e.g. the toggle button) — pass `{ onClick, type: "button" }` so
   * the widget's handler attaches to whatever element the embedder declared as the root.
   * Ignored when no template is registered (default JSX already wires its own handlers).
   * Applied as DEFAULTS: properties on the template root (literal HTML attrs or onClick
   * injected by `data-custom-event` on the root) win on key conflict. This matches the
   * "data-custom-event wins over registry defaults" precedence used for nested interactive
   * slots — so an embedder can opt out of the widget's default action by putting
   * `data-custom-event` on the templated root.
   */
  rootProps?: Record<string, unknown>;
}

/**
 * Wrap a component's render in `<Templated id="lastbot-…">` to make it overridable by a
 * host-page `<template id="…">`. Mark the dynamic regions inside the children with
 * `<Slot name="…">defaultContent</Slot>`.
 *
 * - No host template registered → renders children unchanged (Slot is transparent).
 * - Host template registered → harvests slot content from children, then renders the parsed
 *   template tree with each SlotPlaceholder replaced by the harvested content (or its own
 *   fallback if the React tree didn't provide a matching <Slot>). If `rootProps` is set,
 *   those props are cloned onto the template's root element.
 */
export const Templated: FC<TemplatedProps> = ({ id, children, rootProps }) => {
  const tree = useTemplate(id);
  // null-tolerant read — Templated is used inside tests that don't mount a
  // CoreProvider. When there's no core, interactive-slot auto-wiring is
  // a no-op (the embedder's fallback renders with no onClick attached).
  const core = useContext(CoreContext);
  const slotId = templatedSlotId(id);

  // Scope wrapper so descendant <Slot> markers can resolve drop-on-override
  // semantics against the v2 claim map. The wrapper is present in both
  // branches (no template / template active) because the resolution rule
  // is the same — "claimed elsewhere = drop here."
  let content: ReactNode;
  if (tree === undefined) {
    content = children;
  } else {
    const slots = harvestSlots(children);
    const rendered = renderTemplate(tree, slots, core);
    content = rootProps ? applyRootProps(rendered, rootProps) : rendered;
  }

  // `data-slot-name` marker on every shell that maps to a registered slot,
  // so embedders can target both inner slots AND outer template shells the
  // same way in CSS / DOM queries. `lastbot-app` and other non-slot ids
  // render without a wrapper. Source order: rootProps was already applied
  // above; the marker wrap sits outside so rootProps still targets the
  // template's intended root (the embedder's first element), not the marker.
  const wrapped = slotId !== null
    ? <span data-slot-name={slotId}>{content}</span>
    : content;

  return (
    <TemplatedScopeProvider templateId={id}>{wrapped}</TemplatedScopeProvider>
  );
};

/**
 * Merge `rootProps` onto the first React element in the rendered template tree.
 *
 * Templates often have whitespace text nodes at the top level (just from author
 * indentation in the HTML — `<template>\n  <button>…</button>\n</template>`). The
 * parsed result is then an array like [" ", <button>, " "], not a single element.
 * We walk the array, skip non-element nodes (whitespace, numbers, null), and clone
 * the first element we find with the provided props.
 *
 * If the template has multiple top-level elements, `rootProps` lands on the first one.
 * Embedders should write a single root element for templates that need handlers.
 */
/**
 * Treat `rootProps` as defaults: only keys not already on the rendered element
 * are applied. Without this, an `onClick` injected by `renderTemplate` for
 * `data-custom-event` on the template root would be clobbered by `rootProps.onClick`
 * (e.g. the toggle-button's default open-chat handler), silently dropping the
 * embedder's custom event. Matches the "data-custom-event wins over registry
 * defaults" precedence enforced for nested interactive slots.
 */
const mergeRootProps = (
  element: ReactElement,
  props: Record<string, unknown>,
): Record<string, unknown> => {
  const existing = element.props as Record<string, unknown>;
  const merged: Record<string, unknown> = {};
  for (const key of Object.keys(props)) {
    if (existing[key] === undefined) {
      merged[key] = props[key];
    }
  }
  return merged;
};

const applyRootProps = (
  node: ReactNode,
  props: Record<string, unknown>,
): ReactNode => {
  if (isValidElement(node)) {
    return cloneElement(node as ReactElement, mergeRootProps(node, props));
  }
  if (Array.isArray(node)) {
    let applied = false;
    const out = node.map(child => {
      if (!applied && isValidElement(child)) {
        applied = true;
        return cloneElement(child as ReactElement, mergeRootProps(child, props));
      }
      return child;
    });
    if (!applied) {
      // Array contained only text / boolean / null entries — no element to
      // attach onClick etc. to. The widget would silently render text with
      // no handler. Warn so the embedder catches it during dev.
      warnRootPropsLost();
    }
    return out;
  }
  // Pure text / number / null / boolean root → nothing to clone props onto.
  // Common authoring mistake: `<template id="lastbot-toggle-button">Open chat</template>`
  // renders the text with no click handler. Surface this loudly in dev.
  warnRootPropsLost();
  return node;
};

let rootPropsWarned = false;
const warnRootPropsLost = (): void => {
  if (rootPropsWarned) return;
  rootPropsWarned = true;
  console.warn(
    "[lastbot-chat] A template's root content is not a React element — " +
      "the widget's onClick / keyboard handlers / data-testid could not be " +
      "attached. Wrap the template content in a single element. " +
      "Example: `<template id=\"lastbot-toggle-button\"><button>Open chat</button></template>`.",
  );
};

/**
 * Harvest marker for a `<Slot hidden>`: the slot is present (so the
 * template's fallback must not stand in for it) but renders nothing,
 * not even a per-slot override.
 */
export const HIDDEN_SLOT: unique symbol = Symbol("lastbot.hidden-slot");

type HarvestedSlots = Map<string, ReactNode | typeof HIDDEN_SLOT>;

const harvestSlots = (children: ReactNode): HarvestedSlots => {
  const slots: HarvestedSlots = new Map();
  walkForSlots(children, slots);
  return slots;
};

const walkForSlots = (node: ReactNode, slots: HarvestedSlots): void => {
  if (node === null || node === undefined || typeof node === "boolean") return;
  if (Array.isArray(node)) {
    for (const child of node) walkForSlots(child, slots);
    return;
  }
  if (!isValidElement(node)) return;

  if (node.type === Slot) {
    const props = node.props as { name: string; children?: ReactNode; hidden?: boolean };
    // A hidden slot's children are never rendered, so neither its content
    // nor any Slot nested in it is harvested.
    if (props.hidden === true) {
      slots.set(props.name, HIDDEN_SLOT);
      return;
    }
    // Register this slot's default content. Last-wins on duplicate ids; the
    // registry's uniqueness contract (Phase 2) catches actual collisions.
    slots.set(props.name, props.children ?? null);
    // Continue descending into the slot's default content so nested
    // <Slot name="…"> markers inside it are independently harvestable. This
    // lets an embedder relocate an inner element (e.g. `<slot name="logo">`
    // declared on its own in the embedder's template) without having to
    // rewrite the entire surrounding parent slot.
    if (props.children !== undefined) {
      walkForSlots(props.children, slots);
    }
    return;
  }

  const props = node.props as { children?: ReactNode };
  if (props.children !== undefined) {
    walkForSlots(props.children, slots);
  }
};

const renderTemplate = (
  tree: ReactNode,
  slots: HarvestedSlots,
  core: LastBotCore | null,
): ReactNode => {
  if (tree === null || tree === undefined || typeof tree === "boolean") return null;
  if (typeof tree === "string" || typeof tree === "number") return tree;
  if (Array.isArray(tree)) {
    return tree.map(node => renderTemplate(node, slots, core));
  }
  if (!isValidElement(tree)) return null;

  if (tree.type === SlotPlaceholder) {
    const props = tree.props as { name: string; fallback?: ReactNode };
    const slotted = slots.get(props.name);
    // Hidden: no fallback and no per-slot Templated, so neither the
    // container's fallback nor a `lastbot-X` override can render.
    if (slotted === HIDDEN_SLOT) return null;
    // Harvested widget content wins (per-template projection — the embedder
    // overrode a template like lastbot-navbar and the original JSX subtree
    // provided <Slot name="X">). No interactive wrapping needed; the
    // harvested element already has its onClick attached. For non-harvested
    // slots, fall back to the embedder's template-side content with
    // wireInteractiveFallback to attach the slot's onClick if applicable.
    const content =
      slotted !== undefined
        ? slotted
        : wireInteractiveFallback(props.name, props.fallback ?? null, core);
    // Per-slot Templated wrap only when no shell exists for this slot id —
    // mirrors Slot.tsx's non-templated path. Shells already carry the
    // marker + template via their own internal Templated; wrapping here
    // would double the marker and break harvest (see the long comment in
    // Slot.tsx).
    return hasShellTemplate(props.name) ? (
      <>{content}</>
    ) : (
      <Templated id={`lastbot-${props.name}`}>{content}</Templated>
    );
  }

  const props = tree.props as Record<string, unknown> & { children?: ReactNode };

  // Embedder-declared `data-custom-event` on ANY element → attach an onClick
  // that dispatches the named CustomEvent on `<lastbot-chat>`. Embedders
  // listen via `document.addEventListener("send-email", …)` (events bubble
  // and compose out of the shadow root). No registry lookup, no default
  // action — pure embedder-controlled signaling.
  //
  // Checked BEFORE the recursion into children so it fires regardless of
  // whether the element wraps a SlotPlaceholder. If the same element is also
  // the root of a registered interactive slot's fallback, the custom-event
  // onClick wins — the embedder is opting out of the widget's default.
  const customEvent = props["data-custom-event"];
  const renderedChildren =
    props.children === undefined
      ? null
      : Children.toArray(
          Array.isArray(props.children)
            ? props.children.map(c => renderTemplate(c, slots, core))
            : [renderTemplate(props.children, slots, core)],
        );

  if (typeof customEvent === "string" && core !== null) {
    // Surface the double-dispatch footgun: this element will fire the
    // custom event, AND any SlotPlaceholder for an interactive slot in its
    // subtree will fire its own registry event when clicked. Single user
    // click → two events. Warn once per offending event name.
    if (props.children !== undefined && containsInteractiveSlot(props.children)) {
      warnAncestorCustomEvent(customEvent);
    }
    const payload = props["data-custom-event-payload"];
    const dataset = extractDataset(props);
    const onClick = (): void => {
      dispatchCustomTemplateEvent(core, customEvent, {
        payload: typeof payload === "string" ? payload : undefined,
        dataset,
      });
    };
    return renderedChildren === null
      ? cloneElement(tree as ReactElement, { onClick })
      : cloneElement(tree as ReactElement, { onClick }, ...renderedChildren);
  }

  if (renderedChildren === null) return tree;
  return cloneElement(tree as ReactElement, undefined, ...renderedChildren);
};

/**
 * Walks a parsed-template subtree (React JSX, not rendered fibers) looking
 * for any `SlotPlaceholder` whose registry entry is `kind: "interactive"`.
 * Used by the data-custom-event ancestor check to warn about double-dispatch.
 */
const containsInteractiveSlot = (node: ReactNode): boolean => {
  if (node === null || node === undefined || typeof node === "boolean") return false;
  if (Array.isArray(node)) return node.some(containsInteractiveSlot);
  if (!isValidElement(node)) return false;
  if (node.type === SlotPlaceholder) {
    const name = (node.props as { name?: string }).name;
    if (typeof name === "string") {
      const descriptor = getSlotDescriptor(name);
      if (descriptor?.kind === "interactive") return true;
    }
  }
  const childProps = node.props as { children?: ReactNode; fallback?: ReactNode };
  if (childProps.children !== undefined && containsInteractiveSlot(childProps.children)) return true;
  if (childProps.fallback !== undefined && containsInteractiveSlot(childProps.fallback)) return true;
  return false;
};

const warnedAncestorEvents = new Set<string>();
const warnAncestorCustomEvent = (eventName: string): void => {
  if (warnedAncestorEvents.has(eventName)) return;
  warnedAncestorEvents.add(eventName);
  console.warn(
    `[lastbot-chat] data-custom-event="${eventName}" is on an element that ` +
      "contains an interactive slot. A click will fire BOTH the custom event " +
      "AND the slot's registry default. Place data-custom-event on a sibling " +
      "of the slot (or on the slot's fallback root) if you want one action.",
  );
};

/**
 * Pull every `data-*` prop off a parsed-template element's props (except
 * the two `data-custom-event-*` plumbing attrs) and return a camelCased
 * map that mirrors `HTMLElement.dataset`. `data-thread-id` → `threadId`.
 *
 * Operates on React props (post-`htmlAttrsToReactProps`), not on a real
 * DOM element — the element doesn't exist yet at template-parse time.
 */
const extractDataset = (
  props: Record<string, unknown>,
): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const key of Object.keys(props)) {
    if (!key.startsWith("data-")) continue;
    if (key === "data-custom-event" || key === "data-custom-event-payload") continue;
    const value = props[key];
    if (typeof value !== "string") continue;
    const camel = key
      .slice("data-".length)
      .replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
    out[camel] = value;
  }
  return out;
};

/**
 * For an interactive slot's embedder-supplied fallback, attach the slot's
 * onClick so clicks fire the registry's event + default action. Display
 * slots and containers pass the fallback through unchanged.
 *
 * Single React-element fallback → cloneElement to add onClick directly.
 * Multi-element / text fallback → wrap in a `display: contents` span that
 * catches bubbling clicks without affecting layout, with role="button" +
 * tabIndex=0 + Enter/Space keyDown for keyboard accessibility.
 * `core` may be null in unit tests; in that case the fallback renders as-is.
 *
 * Precedence: when the fallback's root element carries `data-custom-event`,
 * the embedder is explicitly opting out of the registry default. Dispatch
 * the custom event instead; the slot's onClick is NOT attached.
 *
 * The `name` param is typed `string` for ergonomic callers, but reaches
 * `runSlotAction` only after the descriptor.kind === "interactive" guard
 * narrows it to a registered `SlotId`. The cast at the call below documents
 * that invariant; if the guard ever changes, the type-check still passes
 * but the runtime warning in `dispatchSlotEvent` catches unknown ids.
 */
const wireInteractiveFallback = (
  name: string,
  fallback: ReactNode,
  core: LastBotCore | null,
): ReactNode => {
  if (fallback === null) return null;
  const descriptor = getSlotDescriptor(name);
  if (descriptor?.kind !== "interactive" || core === null) {
    return fallback;
  }
  // `name` is now a registered slot id whose descriptor.kind === "interactive"
  // — narrow once here so the rest of the function uses SlotId.
  const slotId = name as SlotId;

  // data-custom-event override → the embedder owns this click; the
  // registry default does not run. The fallback is whatever parseTemplate
  // produced for the slot's inner content — usually a single React element
  // (single child) but can be an array (multiple top-level children).
  const fallbackRoot = isValidElement(fallback)
    ? fallback
    : Array.isArray(fallback)
      ? fallback.find(isValidElement) ?? null
      : null;
  if (fallbackRoot !== null && isValidElement(fallbackRoot)) {
    const fallbackProps = fallbackRoot.props as Record<string, unknown>;
    const customEvent = fallbackProps["data-custom-event"];
    if (typeof customEvent === "string") {
      const payload = fallbackProps["data-custom-event-payload"];
      const dataset = extractDataset(fallbackProps);
      const onClick = (): void => {
        dispatchCustomTemplateEvent(core, customEvent, {
          payload: typeof payload === "string" ? payload : undefined,
          dataset,
        });
      };
      // Clone just the root element with the new onClick; sibling children
      // (rare — most slots have a single root) pass through unchanged.
      if (Array.isArray(fallback)) {
        return fallback.map(child =>
          child === fallbackRoot
            ? cloneElement(fallbackRoot as ReactElement, { onClick })
            : child,
        );
      }
      return cloneElement(fallbackRoot as ReactElement, { onClick });
    }
  }

  const handleClick = (_event?: MouseEvent): void => {
    runSlotAction(core, slotId);
  };
  if (isValidElement(fallback)) {
    return cloneElement(fallback as ReactElement, { onClick: handleClick });
  }
  // Multi-element / text fallback wrapped in a clickable shell. Add
  // keyboard + screen-reader semantics so the span doesn't degrade from
  // the registry slot's native interactive role.
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    // Native buttons would map Enter/Space → click on their own; this
    // wrapper is only entered when the fallback is NOT a single element,
    // so a native button at the root isn't possible here. Still gate on
    // tagName to be safe against future fallback shapes.
    if (event.currentTarget.tagName === "BUTTON") return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick();
    }
  };
  return (
    <span
      style={{ display: "contents" }}
      role="button"
      tabIndex={0}
      onClick={handleClick as () => void}
      onKeyDown={handleKeyDown}
    >
      {fallback}
    </span>
  );
};
